import { inputClass } from '@/components/base/Field';

export interface TestEntry {
  key: string;
  scope: string;
  difficulty: string;
  score: string;
}

export const DIFFICULTY_OPTIONS = ['하', '중', '상', '최상'];

let counter = 0;

export function newTestEntry(): TestEntry {
  counter += 1;
  return { key: `test-${Date.now()}-${counter}`, scope: '', difficulty: '중', score: '' };
}

interface TestRecordEditorProps {
  tests: TestEntry[];
  onChange: (tests: TestEntry[]) => void;
}

export default function TestRecordEditor({ tests, onChange }: TestRecordEditorProps) {
  const update = (key: string, patch: Partial<TestEntry>) => {
    onChange(tests.map((t) => (t.key === key ? { ...t, ...patch } : t)));
  };

  const add = () => onChange([...tests, newTestEntry()]);

  const remove = (key: string) => onChange(tests.filter((t) => t.key !== key));

  return (
    <div>
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div>
          <p className="text-sm font-medium text-foreground-800">테스트 기록</p>
          <p className="mt-0.5 text-xs text-foreground-500">
            이번 수업에서 본 테스트의 범위·난이도·점수를 기록해요.
          </p>
        </div>
        <button
          type="button"
          onClick={add}
          className="flex items-center gap-1 rounded-md border border-primary-300 bg-primary-50 px-3 py-1.5 text-xs font-semibold text-primary-700 transition hover:bg-primary-100 whitespace-nowrap"
        >
          <i className="ri-add-line"></i>
          테스트 추가
        </button>
      </div>

      {tests.length === 0 ? (
        <p className="mt-3 rounded-md border border-dashed border-background-300 px-3 py-4 text-center text-xs text-foreground-500">
          아직 추가된 테스트가 없어요. 필요하면 위 버튼으로 추가해보세요.
        </p>
      ) : (
        <div className="mt-3 flex flex-col gap-2">
          {tests.map((t) => (
            <div key={t.key} className="flex flex-col gap-2 sm:flex-row sm:items-center">
              <input
                type="text"
                value={t.scope}
                onChange={(e) => update(t.key, { scope: e.target.value })}
                placeholder="테스트 범위 (예: 이차방정식 15문제)"
                className={`${inputClass} flex-1`}
              />
              <select
                value={t.difficulty}
                onChange={(e) => update(t.key, { difficulty: e.target.value })}
                aria-label="난이도"
                className={`${inputClass} w-full sm:w-28`}
              >
                {DIFFICULTY_OPTIONS.map((d) => (
                  <option key={d} value={d}>
                    난이도 {d}
                  </option>
                ))}
              </select>
              <div className="flex items-center gap-2">
                <input
                  type="text"
                  value={t.score}
                  onChange={(e) => update(t.key, { score: e.target.value })}
                  placeholder="점수"
                  className={`${inputClass} w-full sm:w-24`}
                />
                <button
                  type="button"
                  onClick={() => remove(t.key)}
                  aria-label="테스트 삭제"
                  className="flex h-9 w-9 shrink-0 items-center justify-center rounded-md text-foreground-400 transition hover:bg-background-200 hover:text-accent-700"
                >
                  <i className="ri-delete-bin-line"></i>
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}